/**
 * Labstep
 *
 * @module state/middleware/timeout-logout
 * @desc Fires logout action if time has expired
 */

import { ORGANIZATION_LOGOUT_TIMEOUTS } from 'labstep-web/constants/organization-logout-timeout';
import { selectAuthenticatedUserEntity } from 'labstep-web/state/selectors/authenticated-user';

export const LAST_ACTION_TIMESTAMP_KEY =
  'labstep_last_action_timestamp';
export const LOGOUT_MARKER_KEY = 'labstep_logout_marker';

const getLastActionTimestamp = (): number | null => {
  const timestamp = localStorage.getItem(LAST_ACTION_TIMESTAMP_KEY);
  if (!timestamp) {
    return null;
  }
  const parsedTimestamp = parseInt(timestamp, 10);
  return Number.isNaN(parsedTimestamp) ? null : parsedTimestamp;
};

const setLastActionTimestamp = (timestamp: number): void => {
  localStorage.setItem(
    LAST_ACTION_TIMESTAMP_KEY,
    timestamp.toString(),
  );
};

const setLogoutMarker = (timestamp: number): void => {
  localStorage.setItem(LOGOUT_MARKER_KEY, timestamp.toString());
};

const clearLogoutMarker = (): void => {
  localStorage.removeItem(LOGOUT_MARKER_KEY);
};

const hasLogoutMarker = (): boolean => {
  return localStorage.getItem(LOGOUT_MARKER_KEY) !== null;
};

const clearLastActionTimestamp = (): void => {
  localStorage.removeItem(LAST_ACTION_TIMESTAMP_KEY);
};

export const logoutAction = {
  type: 'START_LOGOUT',
  meta: {
    reason: 'TIMEOUT_LOGOUT',
  },
};

const getOrganizationTimeout = (state: any): number | null => {
  const authenticatedUser = (state as any).authenticatedUser.byId;
  if (!authenticatedUser?.id) {
    return null;
  }

  const authenticatedUserEntity =
    selectAuthenticatedUserEntity(state);
  const identifier =
    authenticatedUserEntity.userOrganization?.organization.identifier;
  if (!identifier) {
    return null;
  }

  const organizationTimeoutConfig =
    ORGANIZATION_LOGOUT_TIMEOUTS[
      identifier as keyof typeof ORGANIZATION_LOGOUT_TIMEOUTS
    ];
  if (!organizationTimeoutConfig) {
    return null;
  }

  return organizationTimeoutConfig[
    authenticatedUserEntity.userOrganization.type
  ];
};

export const timeoutLogout =
  (store: any) => (next: any) => (action: any) => {
    const state = store.getState();

    // Clear logout marker on successful login
    if (action.type === 'SUCCESS_INTERNAL_LOGIN') {
      clearLogoutMarker();
      return next(action);
    }

    // Early exit for non-REQUEST/PREPARE actions or LOGOUT actions
    if (
      !(
        action.type.includes('REQUEST') ||
        action.type.includes('PREPARE')
      ) ||
      action.type.includes('_LOGOUT')
    ) {
      if (!action.type.includes('WEBSOCKET')) {
        setLastActionTimestamp(Date.now());
      }

      return next(action);
    }

    const timeout = getOrganizationTimeout(state);
    if (!timeout) {
      return next(action);
    }

    // Check for logout marker first - if present, logout immediately
    if (
      hasLogoutMarker() &&
      !(
        action.type.includes('LOGOUT') ||
        action.type.includes('LOGIN')
      )
    ) {
      return next(logoutAction);
    }

    const lastActionTimestamp = getLastActionTimestamp();
    const timestamp = Date.now();
    if (lastActionTimestamp === null) {
      setLastActionTimestamp(timestamp);
      return next(action);
    }

    // Check if timeout has expired BEFORE updating timestamp
    if (Date.now() - lastActionTimestamp > timeout) {
      clearLastActionTimestamp();
      setLogoutMarker(timestamp); // Set marker for other tabs
      return next(logoutAction);
    }

    // Timeout not expired - update timestamp and continue
    setLastActionTimestamp(Date.now());
    return next(action);
  };
